import { FocusEvent, ReactElement, useCallback } from 'react';
import { Select, MenuItem, FormControl, ListItemIcon } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMoreRounded, CheckCircleRounded, AddRounded } from '@mui/icons-material';
import { useField } from 'formik';

const StyledFormControl = styled(FormControl)({
  width: '100%',
  '& div.Mui-disabled': {
    background: '#ccc',
  },
  '&:visited': {
    outline: '2px solid #007bbd',
  },
  '&:focus': {
    outline: '2px solid #007bbd',
  },
  '&:focus-visible': {
    outline: '2px solid #007bbd',
  },
});

export interface IDropDownProps {
  activeSelectionCount?: number;
  afterChange?: () => void;
  className?: string;
  displayEmpty?: boolean;
  fullWidth?: boolean;
  icons?: boolean;
  idProp?: string;
  isDisabled?: boolean;
  prefixField?: string;
  items?: any[];
  labelProp?: string;
  name?: string;
  onBlur?: (event: FocusEvent) => void;
  onChange?: (event) => void;
  onClose?: () => void;
  onOpen?: () => void;
  placeholder?: string;
  required?: boolean;
}

export interface IDropdownType {
  Id: string;
  Title: string;
}

const Dropdown = (props: IDropDownProps): ReactElement => {
  const {
    afterChange,
    activeSelectionCount,
    className,
    displayEmpty,
    fullWidth,
    isDisabled,
    prefixField,
    items,
    icons,
    labelProp,
    idProp,
    required,
    placeholder,
    name,
    onBlur,
    onChange,
    onClose,
    onOpen,
  } = props;
  const idKey = idProp || 'id';
  const labelKey = labelProp || 'name';

  const [field] = useField(name);
  const selection = activeSelectionCount === undefined ? field?.value : field?.value?.at(-1);

  const handleChange = useCallback(
    (e) => {
      if (typeof onChange !== 'undefined') {
        onChange(e);
      } else {
        field.onChange(e);
      }
      afterChange?.();
    },
    [afterChange, field, onChange],
  );

  return (
    <>
      <StyledFormControl disabled={isDisabled} fullWidth={fullWidth}>
        <Select
          className={className}
          defaultValue=""
          disabled={isDisabled}
          displayEmpty={displayEmpty}
          fullWidth={fullWidth}
          aria-required={required}
          variant="outlined"
          MenuProps={{
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'bottom',
            },
            slotProps: {
              list: {
                sx: {
                  backgroundColor: '#fff',
                  border: '0.03125rem solid #dedede',
                  borderRadius: 0,
                  padding: 0,
                },
              },
              paper: {
                sx: {
                  borderRadius: 0,
                  boxShadow: 'none',
                  padding: 0,
                },
              },
            },
          }}
          IconComponent={ExpandMoreRounded}
          inputProps={{
            sx: {
              '& .MuiSelect-icon': {
                fill: '#373737',
              },
              '& .MuiSelect-iconOpen': {
                fill: '#007bbd',
              },
            },
            title: selection || labelKey,
          }}
          {...field}
          value={selection || ''}
          onBlur={onBlur || field.onBlur}
          onChange={handleChange}
          onClose={onClose}
          onOpen={onOpen}
        >
          {placeholder && (
            <MenuItem disabled value="">
              {placeholder}
            </MenuItem>
          )}
          {items?.map((item) => {
            return (
              <MenuItem key={item[idKey]} value={item[idKey]}>
                {icons && (
                  <ListItemIcon>
                    {selection === item[idKey] ? (
                      <CheckCircleRounded sx={{ color: '#82bc00' }} />
                    ) : (
                      <AddRounded
                        sx={{
                          border: 0,
                          borderRadius: '50%',
                          boxShadow: '1px 3px 8px 2px #00000019',
                          color: '#82bc00',
                          height: '1.5rem',
                          minWidth: '1.5rem',
                          width: '1.5rem',
                        }}
                      />
                    )}
                  </ListItemIcon>
                )}
                {prefixField ? (item[prefixField] || '') + ' - ' + (item[labelKey] || '') : item[labelKey]}
              </MenuItem>
            );
          })}
        </Select>
      </StyledFormControl>
    </>
  );
};

export default Dropdown;
